import React, { useEffect } from 'react';

const CompositePattern = () => {
  useEffect(() => {
    // SEO optimization
    document.title = 'Composite Pattern - Structural Design Pattern Guide 2024';
    
    // Add meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Master the Composite Pattern - compose objects into tree structures to represent part-whole hierarchies. Learn with file system and UI component examples.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'Master the Composite Pattern - compose objects into tree structures to represent part-whole hierarchies. Learn with file system and UI component examples.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    // Add keywords meta tag
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'composite pattern, design patterns, structural patterns, tree structure, part-whole hierarchy, software design');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'keywords';
      meta.content = 'composite pattern, design patterns, structural patterns, tree structure, part-whole hierarchy, software design';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }

    return () => {
      // Cleanup - restore original title
      document.title = 'SlazySloth - Deep Dives';
    };
  }, []);

  const styles = {
    container: {
      width: '100%',
      margin: 0,
      padding: '2rem',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
      lineHeight: 1.6,
      color: '#333',
      background: '#ffffff',
      fontSize: '16px'
    },
    header: {
      marginBottom: '2rem'
    },
    title: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '1rem'
    },
    subtitle: {
      fontSize: '16px',
      color: '#333',
      marginBottom: '1rem',
      fontWeight: 400
    },
    content: {
      fontSize: '16px',
      lineHeight: 1.6
    },
    heading: {
      fontSize: '16px',
      fontWeight: 600,
      color: '#333',
      margin: '1.5rem 0 1rem 0'
    },
    paragraph: {
      marginBottom: '1rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    list: {
      marginBottom: '1rem',
      paddingLeft: '1.5rem'
    },
    listItem: {
      marginBottom: '0.5rem',
      color: '#333',
      fontSize: '16px',
      lineHeight: 1.6
    },
    code: {
      background: '#f5f5f5',
      padding: '0.2rem 0.4rem',
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#333'
    },
    codeBlock: {
      background: '#f5f5f5',
      padding: '1rem',
      fontFamily: 'monospace',
      fontSize: '14px',
      color: '#333',
      marginBottom: '1rem',
      whiteSpace: 'pre-wrap',
      overflow: 'auto'
    },
    blockquote: {
      borderLeft: '4px solid #008080',
      paddingLeft: '1rem',
      margin: '1rem 0',
      fontStyle: 'italic',
      color: '#555'
    }
  };

  return (
    <article style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Composite Pattern</h1>
        <p style={styles.subtitle}>
          Structural Design Pattern - Compose objects into tree structures
        </p>
      </header>

      <main style={styles.content}>
        <section>
          <h2 style={styles.heading}>Intent</h2>
          <p style={styles.paragraph}>
            The Composite pattern allows you to compose objects into tree structures to represent 
            part-whole hierarchies. It lets clients treat individual objects and compositions of 
            objects uniformly, enabling recursive composition.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Problem</h2>
          <p style={styles.paragraph}>
            You need to work with tree-like structures where individual objects and groups of 
            objects should be treated uniformly. You want to perform operations on both individual 
            items and collections without distinguishing between them.
          </p>
        </section>

        <section>
          <h2 style={styles.heading}>Real-World Example: File System</h2>
          <div style={styles.codeBlock}>
{`// Component interface
interface FileSystemComponent {
    void display(int depth);
    long getSize();
    String getName();
}

// Leaf - represents individual files
class File implements FileSystemComponent {
    private String name;
    private long size;
    
    public File(String name, long size) {
        this.name = name;
        this.size = size;
    }
    
    @Override
    public void display(int depth) {
        String indent = "  ".repeat(depth);
        System.out.println(indent + "📄 " + name + " (" + size + " bytes)");
    }
    
    @Override
    public long getSize() {
        return size;
    }
    
    @Override
    public String getName() {
        return name;
    }
}

// Composite - represents directories that can contain files and other directories
class Directory implements FileSystemComponent {
    private String name;
    private List<FileSystemComponent> children;
    
    public Directory(String name) {
        this.name = name;
        this.children = new ArrayList<>();
    }
    
    public void add(FileSystemComponent component) {
        children.add(component);
    }
    
    public void remove(FileSystemComponent component) {
        children.remove(component);
    }
    
    @Override
    public void display(int depth) {
        String indent = "  ".repeat(depth);
        System.out.println(indent + "📁 " + name + "/");
        
        for (FileSystemComponent child : children) {
            child.display(depth + 1);
        }
    }
    
    @Override
    public long getSize() {
        long totalSize = 0;
        for (FileSystemComponent child : children) {
            totalSize += child.getSize();
        }
        return totalSize;
    }
    
    @Override
    public String getName() {
        return name;
    }
    
    public List<FileSystemComponent> getChildren() {
        return new ArrayList<>(children);
    }
}

// Usage
public class CompositePatternDemo {
    public static void main(String[] args) {
        // Create files
        File readme = new File("README.md", 1024);
        File config = new File("config.json", 512);
        File main = new File("Main.java", 2048);
        File test = new File("Test.java", 1536);
        
        // Create directories
        Directory root = new Directory("project");
        Directory src = new Directory("src");
        Directory tests = new Directory("tests");
        Directory docs = new Directory("docs");
        
        // Build directory structure
        root.add(readme);
        root.add(config);
        root.add(src);
        root.add(tests);
        root.add(docs);
        
        src.add(main);
        tests.add(test);
        
        File userGuide = new File("user-guide.pdf", 4096);
        docs.add(userGuide);
        
        // Display entire structure
        System.out.println("File System Structure:");
        root.display(0);
        
        System.out.println("\\nTotal size: " + root.getSize() + " bytes");
        
        // Treat individual files and directories uniformly
        System.out.println("\\nSize of src directory: " + src.getSize() + " bytes");
        System.out.println("Size of README file: " + readme.getSize() + " bytes");
    }
}`}
          </div>
        </section>

        <section>
          <h2 style={styles.heading}>Key Benefits</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Uniform Treatment:</strong> Treat individual and composite objects the same way</li>
            <li style={styles.listItem}><strong>Recursive Composition:</strong> Build complex tree structures easily</li>
            <li style={styles.listItem}><strong>Flexibility:</strong> Easy to add new types of components</li>
            <li style={styles.listItem}><strong>Client Simplicity:</strong> Clients don't need to distinguish between leaf and composite</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>When to Use</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>When you need to represent part-whole hierarchies</li>
            <li style={styles.listItem}>When you want clients to ignore differences between individual and composite objects</li>
            <li style={styles.listItem}>When you're working with tree structures</li>
            <li style={styles.listItem}>When you need recursive composition</li>
          </ul>
        </section>

        <section>
          <h2 style={styles.heading}>Conclusion</h2>
          <p style={styles.paragraph}>
            The Composite pattern is perfect for representing hierarchical structures where you 
            need to treat individual objects and collections uniformly. It's commonly used in 
            GUI frameworks, file systems, and organizational structures.
          </p>
        </section>
      </main>
    </article>
  );
};

export default CompositePattern;
